import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import {
  User,
  pricelistsAPI,
  usersAPI,
  pricelistUserAssignmentsAPI,
} from '../../services/api';

interface PricelistUserAssignment {
  id: number;
  user_id: number;
  billable_rate: number;
  cost_rate: number;
  user: {
    id: number;
    full_name: string;
    email: string;
    role: string;
  };
}

interface PricelistDetail {
  id: number;
  name: string;
  description?: string;
  currency: 'CAD' | 'USD';
  effective_date: string;
  scope_description: string;
  created_at?: string;
  updated_at?: string;
  client?: {
    id: number;
    name: string;
    company?: string;
  } | null;
  project?: {
    id: number;
    name: string;
    is_internal: boolean;
  } | null;
  user_assignments: PricelistUserAssignment[];
}

interface PricelistDetailViewProps {
  pricelistId: number;
  onBack: () => void;
  isNewPricelist?: boolean;
}

interface BulkUserRate {
  user_id: number;
  user_name: string;
  user_email: string;
  billable_rate: string;
  cost_rate: string;
  selected: boolean;
}

const PricelistDetailView: React.FC<PricelistDetailViewProps> = ({
  pricelistId,
  onBack,
  isNewPricelist = false,
}) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { isDark } = useTheme();
  const [pricelist, setPricelist] = useState<PricelistDetail | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newUserRates, setNewUserRates] = useState({
    billable_rate: '',
    cost_rate: '',
  });
  // Bulk user rates state
  const [bulkUserRates, setBulkUserRates] = useState<BulkUserRate[]>([]);
  const [defaultBillableRate, setDefaultBillableRate] = useState('');
  const [defaultCostRate, setDefaultCostRate] = useState('');
  const [addingBulk, setAddingBulk] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pricelistResponse, usersResponse] = await Promise.all([
        pricelistsAPI.getById(pricelistId),
        usersAPI.getAll(),
      ]);

      const pricelistData = pricelistResponse.data.pricelist;
      const usersData = usersResponse.data.users || [];

      setPricelist(pricelistData);
      setAvailableUsers(usersData);

      // Initialize bulk user rates for unassigned users
      const assignedUserIds = (pricelistData.user_assignments || []).map(
        (a: PricelistUserAssignment) => a.user_id,
      );
      const unassigned = usersData.filter(
        (user: User) => !assignedUserIds.includes(user.id),
      );
      setBulkUserRates(
        unassigned.map((user: User) => ({
          user_id: user.id,
          user_name: user.full_name,
          user_email: user.email,
          billable_rate: '',
          cost_rate: '',
          selected: false,
        })),
      );
    } catch (error: any) {
      setError(error.response?.data?.error || t('pricelistDetail.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pricelistId]);

  // Update bulk rates when default rates change
  const applyDefaultRatesToSelected = () => {
    setBulkUserRates((prev) =>
      prev.map((user) => ({
        ...user,
        billable_rate:
          user.selected && defaultBillableRate
            ? defaultBillableRate
            : user.billable_rate,
        cost_rate:
          user.selected && defaultCostRate ? defaultCostRate : user.cost_rate,
      })),
    );
  };

  const toggleUserSelection = (userId: number) => {
    setBulkUserRates((prev) =>
      prev.map((user) =>
        user.user_id === userId ? { ...user, selected: !user.selected } : user,
      ),
    );
  };

  const selectAllUsers = () => {
    setBulkUserRates((prev) =>
      prev.map((user) => ({ ...user, selected: true })),
    );
  };

  const deselectAllUsers = () => {
    setBulkUserRates((prev) =>
      prev.map((user) => ({ ...user, selected: false })),
    );
  };

  const updateUserRate = (
    userId: number,
    field: 'billable_rate' | 'cost_rate',
    value: string,
  ) => {
    setBulkUserRates((prev) =>
      prev.map((user) =>
        user.user_id === userId ? { ...user, [field]: value } : user,
      ),
    );
  };

  const handleBulkAddUsers = async () => {
    const usersToAdd = bulkUserRates.filter(
      (user) => user.selected && user.billable_rate && user.cost_rate,
    );

    if (usersToAdd.length === 0) {
      setError(t('pricelistDetail.selectUsersWithRates'));
      return;
    }

    setAddingBulk(true);
    setError('');

    try {
      // Add users one by one (could be optimized with a bulk endpoint)
      for (const user of usersToAdd) {
        await pricelistUserAssignmentsAPI.create(pricelistId, {
          user_id: user.user_id,
          billable_rate: parseFloat(user.billable_rate),
          cost_rate: parseFloat(user.cost_rate),
        });
      }

      // Reset and close modal
      setShowAddUser(false);
      setDefaultBillableRate('');
      setDefaultCostRate('');

      // Refresh data
      await fetchData();
    } catch (error: any) {
      setError(
        error.response?.data?.error || t('pricelistDetail.addUserFailed'),
      );
    } finally {
      setAddingBulk(false);
    }
  };

  const selectedUsersCount = bulkUserRates.filter((u) => u.selected).length;
  const validSelectedCount = bulkUserRates.filter(
    (u) => u.selected && u.billable_rate && u.cost_rate,
  ).length;

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await pricelistUserAssignmentsAPI.create(pricelistId, {
        user_id: parseInt(selectedUserId),
        billable_rate: parseFloat(newUserRates.billable_rate),
        cost_rate: parseFloat(newUserRates.cost_rate),
      });

      // Reset form and close modal
      setSelectedUserId('');
      setNewUserRates({ billable_rate: '', cost_rate: '' });
      setShowAddUser(false);

      // Refresh data
      await fetchData();
    } catch (error: any) {
      setError(
        error.response?.data?.error || t('pricelistDetail.addUserFailed'),
      );
    }
  };

  const handleRemoveUser = async (assignmentId: number) => {
    const confirmed = await confirm({
      title: t('pricelistDetail.removeUserTitle'),
      message: t('pricelistDetail.removeUserConfirm'),
      confirmLabel: t('common.remove'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await pricelistUserAssignmentsAPI.delete(pricelistId, assignmentId);

      // Refresh data
      await fetchData();
    } catch (error: any) {
      setError(
        error.response?.data?.error || t('pricelistDetail.removeUserFailed'),
      );
    }
  };

  const handleUpdateRates = async (
    assignmentId: number,
    billableRate: number,
    costRate: number,
  ) => {
    try {
      await pricelistUserAssignmentsAPI.update(pricelistId, assignmentId, {
        billable_rate: billableRate,
        cost_rate: costRate,
      });

      // Refresh data
      await fetchData();
    } catch (error: any) {
      setError(
        error.response?.data?.error || t('pricelistDetail.updateRatesFailed'),
      );
    }
  };

  const getUnassignedUsers = () => {
    if (!pricelist) return availableUsers;
    const assignedUserIds = (pricelist.user_assignments || []).map(
      (assignment) => assignment.user_id,
    );
    return availableUsers.filter((user) => !assignedUserIds.includes(user.id));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!pricelist) {
    return (
      <div className="p-6">
        <div className="text-center text-red-600">
          {t('pricelistDetail.notFound')}
        </div>
      </div>
    );
  }

  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 rounded-2xl'
    : 'bg-white border border-slate-200 rounded-lg shadow-sm';

  const inputClass = isDark
    ? 'w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-md text-white placeholder-slate-400 focus:ring-2 focus:ring-violet-500'
    : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

  const selectClass = isDark
    ? 'w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-md text-white focus:ring-2 focus:ring-violet-500'
    : 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className={`p-6 ${isDark ? 'bg-slate-900 min-h-screen' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className={`flex items-center ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-800'}`}
          >
            <svg
              className="h-5 w-5 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            {t('pricelistDetail.backToPricelists')}
          </button>

          {/* Step 2 Indicator for New Pricelists */}
          {isNewPricelist &&
            (!pricelist?.user_assignments ||
              pricelist.user_assignments.length === 0) && (
              <div className="flex items-center ml-6">
                <div className="flex items-center justify-center w-8 h-8 bg-gray-300 text-gray-600 rounded-full text-sm font-medium">
                  1
                </div>
                <div className="w-16 bg-gray-200 rounded-full h-2 mx-2">
                  <div className="bg-blue-600 h-2 rounded-full w-full"></div>
                </div>
                <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-medium">
                  2
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    {t('pricelistDetail.step2Title')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('pricelistDetail.step2Description')}
                  </p>
                </div>
              </div>
            )}
        </div>

        <button
          onClick={() => setShowAddUser(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
        >
          {t('pricelistDetail.addUser')}
        </button>
      </div>

      {error && (
        <div
          className={`mb-4 p-3 rounded-xl ${isDark ? 'bg-rose-500/10 border border-rose-500/30 text-rose-400' : 'bg-red-100 border border-red-400 text-red-700'}`}
        >
          {error}
        </div>
      )}

      {/* Pricelist Details */}
      <div className={`${cardClass} p-6 mb-6`}>
        <h1
          className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}
        >
          {pricelist.name}
        </h1>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <h3
              className={`text-sm font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
            >
              {t('pricelists.client')}
            </h3>
            <p
              className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              {pricelist.client ? (
                <>
                  {pricelist.client.name}
                  {pricelist.client.company && (
                    <span
                      className={isDark ? 'text-slate-400' : 'text-gray-500'}
                    >
                      {' '}
                      ({pricelist.client.company})
                    </span>
                  )}
                </>
              ) : (
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                  {t('pricelists.organizationWide')}
                </span>
              )}
            </p>
          </div>

          <div>
            <h3
              className={`text-sm font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
            >
              {t('pricelists.project')}
            </h3>
            <p
              className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              {pricelist.project ? (
                <>
                  {pricelist.project.name}
                  {pricelist.project.is_internal && (
                    <span
                      className={isDark ? 'text-slate-400' : 'text-gray-500'}
                    >
                      {' '}
                      ({t('common.internal')})
                    </span>
                  )}
                </>
              ) : (
                <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                  {t('pricelistDetail.allProjects')}
                </span>
              )}
            </p>
          </div>

          <div>
            <h3
              className={`text-sm font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
            >
              {t('pricelists.currency')}
            </h3>
            <p
              className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              {pricelist.currency} -{' '}
              {pricelist.currency === 'CAD'
                ? t('pricelistDetail.canadianDollar')
                : t('pricelistDetail.usDollar')}
            </p>
          </div>

          <div>
            <h3
              className={`text-sm font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
            >
              {t('pricelistDetail.effectiveDate')}
            </h3>
            <p
              className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              {new Date(pricelist.effective_date).toLocaleDateString()}
            </p>
          </div>
        </div>

        {pricelist.description && (
          <div className="mt-6">
            <h3
              className={`text-sm font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
            >
              {t('pricelistDetail.description')}
            </h3>
            <p
              className={`mt-1 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              {pricelist.description}
            </p>
          </div>
        )}

        <div
          className={`mt-4 text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}
        >
          {t('pricelistDetail.created')}:{' '}
          {pricelist.created_at
            ? new Date(pricelist.created_at).toLocaleString()
            : 'N/A'}
          {pricelist.updated_at && (
            <span className="ml-4">
              {t('pricelistDetail.updated')}:{' '}
              {new Date(pricelist.updated_at).toLocaleString()}
            </span>
          )}
        </div>
      </div>

      {/* Users List */}
      <div className={cardClass}>
        <div
          className={`px-6 py-4 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
        >
          <h3
            className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}
          >
            {t('pricelistDetail.assignedUsers')} (
            {pricelist.user_assignments?.length || 0})
          </h3>
        </div>

        {!pricelist.user_assignments ||
        pricelist.user_assignments.length === 0 ? (
          <div className="p-8 text-center">
            <div
              className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}
            >
              <svg
                className={`h-6 w-6 ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </div>
            <h3
              className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}
            >
              {isNewPricelist
                ? t('pricelistDetail.completePricelistSetup')
                : t('pricelistDetail.noUsersAssigned')}
            </h3>
            <p
              className={`mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
            >
              {isNewPricelist
                ? t('pricelistDetail.addTeamMembersDescription')
                : t('pricelistDetail.noUsersAssignedYet')}
            </p>
            <button
              onClick={() => setShowAddUser(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t('pricelistDetail.addYourFirstUser')}
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table
              className={`min-w-full divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}
            >
              <thead className={isDark ? 'bg-slate-900/50' : 'bg-gray-50'}>
                <tr>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                  >
                    {t('pricelistDetail.name')}
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                  >
                    {t('pricelistDetail.costRate')} ({pricelist.currency})
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                  >
                    {t('pricelistDetail.billableRate')} ({pricelist.currency})
                  </th>
                  <th
                    className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                  >
                    {t('common.actions')}
                  </th>
                </tr>
              </thead>
              <tbody
                className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}
              >
                {(pricelist.user_assignments || []).map((assignment) => (
                  <UserRow
                    key={assignment.id}
                    assignment={assignment}
                    currency={pricelist.currency}
                    onUpdate={(billableRate, costRate) =>
                      handleUpdateRates(assignment.id, billableRate, costRate)
                    }
                    onRemove={() => handleRemoveUser(assignment.id)}
                    isDark={isDark}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Users Modal - Bulk Assignment */}
      {showAddUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-10">
          <div
            className={`w-full max-w-4xl p-6 rounded-2xl max-h-[90vh] overflow-hidden flex flex-col ${isDark ? 'bg-slate-900 border border-slate-700' : 'bg-white border border-slate-200 shadow-xl'}`}
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3
                  className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                  {t('pricelistDetail.addUsersToPricelist')}
                </h3>
                <p
                  className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                >
                  {t('pricelistDetail.bulkAddDescription')}
                </p>
              </div>
              <button
                onClick={() => setShowAddUser(false)}
                className={`p-1 rounded-lg transition-colors ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
              >
                <span className="sr-only">{t('common.close')}</span>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Default Rates Section */}
            <div
              className={`rounded-xl p-4 mb-4 ${isDark ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-violet-50 border border-violet-100'}`}
            >
              <h4
                className={`text-sm font-medium mb-3 ${isDark ? 'text-violet-300' : 'text-violet-800'}`}
              >
                {t('pricelistDetail.defaultRates')}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label
                    className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}
                  >
                    {t('pricelistDetail.defaultCostRate')} ({pricelist.currency}
                    )
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={defaultCostRate}
                    onChange={(e) => setDefaultCostRate(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'border-gray-300'} border focus:outline-none focus:ring-2 focus:ring-violet-500`}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label
                    className={`block text-xs font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}
                  >
                    {t('pricelistDetail.defaultBillableRate')} (
                    {pricelist.currency})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={defaultBillableRate}
                    onChange={(e) => setDefaultBillableRate(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'border-gray-300'} border focus:outline-none focus:ring-2 focus:ring-violet-500`}
                    placeholder="0.00"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={applyDefaultRatesToSelected}
                    disabled={!defaultBillableRate && !defaultCostRate}
                    className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('pricelistDetail.applyToSelected')}
                  </button>
                </div>
              </div>
            </div>

            {/* Selection Controls */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-4">
                <button
                  type="button"
                  onClick={selectAllUsers}
                  className={`text-sm ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-800'}`}
                >
                  {t('pricelistDetail.selectAll')}
                </button>
                <button
                  type="button"
                  onClick={deselectAllUsers}
                  className={`text-sm ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-gray-600 hover:text-gray-800'}`}
                >
                  {t('pricelistDetail.deselectAll')}
                </button>
              </div>
              <div
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
              >
                {selectedUsersCount} {t('pricelistDetail.usersSelected')}
                {selectedUsersCount > 0 &&
                  validSelectedCount < selectedUsersCount && (
                    <span className="text-amber-500 ml-2">
                      ({selectedUsersCount - validSelectedCount}{' '}
                      {t('pricelistDetail.missingRates')})
                    </span>
                  )}
              </div>
            </div>

            {/* Users Table */}
            <div
              className={`flex-1 overflow-y-auto rounded-xl ${isDark ? 'border border-slate-700' : 'border border-slate-200'}`}
            >
              {bulkUserRates.length === 0 ? (
                <div
                  className={`p-8 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                >
                  {t('pricelistDetail.allUsersAssigned')}
                </div>
              ) : (
                <table
                  className={`min-w-full divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}
                >
                  <thead
                    className={`sticky top-0 ${isDark ? 'bg-slate-900' : 'bg-gray-50'}`}
                  >
                    <tr>
                      <th
                        className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-12 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                      >
                        <span className="sr-only">{t('common.select')}</span>
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                      >
                        {t('pricelistDetail.name')}
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-32 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                      >
                        {t('pricelistDetail.costRate')} ({pricelist.currency})
                      </th>
                      <th
                        className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider w-32 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                      >
                        {t('pricelistDetail.billableRate')} (
                        {pricelist.currency})
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}
                  >
                    {bulkUserRates.map((user) => (
                      <tr
                        key={user.user_id}
                        className={`${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'} ${user.selected ? (isDark ? 'bg-violet-500/10' : 'bg-violet-50') : ''}`}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={user.selected}
                            onChange={() => toggleUserSelection(user.user_id)}
                            className="h-4 w-4 text-violet-600 focus:ring-violet-500 border-gray-300 rounded"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div
                            className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}
                          >
                            {user.user_name}
                          </div>
                          <div
                            className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                          >
                            {user.user_email}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={user.cost_rate}
                            onChange={(e) =>
                              updateUserRate(
                                user.user_id,
                                'cost_rate',
                                e.target.value,
                              )
                            }
                            className={`w-full px-2 py-1 rounded text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                              user.selected && !user.cost_rate
                                ? 'border-amber-400 bg-amber-50'
                                : isDark
                                  ? 'bg-slate-700 border-slate-600 text-white'
                                  : 'border-gray-300'
                            } border`}
                            placeholder="0.00"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={user.billable_rate}
                            onChange={(e) =>
                              updateUserRate(
                                user.user_id,
                                'billable_rate',
                                e.target.value,
                              )
                            }
                            className={`w-full px-2 py-1 rounded text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                              user.selected && !user.billable_rate
                                ? 'border-amber-400 bg-amber-50'
                                : isDark
                                  ? 'bg-slate-700 border-slate-600 text-white'
                                  : 'border-gray-300'
                            } border`}
                            placeholder="0.00"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer Actions */}
            <div
              className={`flex justify-end space-x-4 pt-4 mt-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
            >
              <button
                type="button"
                onClick={() => setShowAddUser(false)}
                className={`px-4 py-2 rounded-xl transition-colors ${isDark ? 'border border-slate-600 text-slate-300 hover:bg-slate-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleBulkAddUsers}
                disabled={validSelectedCount === 0 || addingBulk}
                className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {addingBulk ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('pricelistDetail.adding')}
                  </>
                ) : (
                  <>
                    {t('pricelistDetail.addSelectedUsers')} (
                    {validSelectedCount})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Separate component for user row with inline editing
interface UserRowProps {
  assignment: PricelistUserAssignment;
  currency: string;
  onUpdate: (billableRate: number, costRate: number) => void;
  onRemove: () => void;
  isDark?: boolean;
}

const UserRow: React.FC<UserRowProps> = ({
  assignment,
  currency,
  onUpdate,
  onRemove,
  isDark = false,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [editRates, setEditRates] = useState({
    billable_rate: Number(assignment.billable_rate || 0).toString(),
    cost_rate: Number(assignment.cost_rate || 0).toString(),
  });

  const handleSave = () => {
    onUpdate(
      parseFloat(editRates.billable_rate),
      parseFloat(editRates.cost_rate),
    );
    setEditing(false);
  };

  const handleCancel = () => {
    setEditRates({
      billable_rate: Number(assignment.billable_rate || 0).toString(),
      cost_rate: Number(assignment.cost_rate || 0).toString(),
    });
    setEditing(false);
  };

  return (
    <tr className={isDark ? 'hover:bg-slate-700/30' : 'hover:bg-gray-50'}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}
        >
          {assignment.user.full_name}
        </div>
        <div
          className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
        >
          {assignment.user.email}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {editing ? (
          <input
            type="number"
            step="0.01"
            min="0"
            value={editRates.cost_rate}
            onChange={(e) =>
              setEditRates({ ...editRates, cost_rate: e.target.value })
            }
            className={`w-24 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-violet-500 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-gray-300'}`}
          />
        ) : (
          <div
            className={`text-sm ${isDark ? 'text-amber-400' : 'text-amber-600'}`}
          >
            {currency} {Number(assignment.cost_rate || 0).toFixed(2)}
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {editing ? (
          <input
            type="number"
            step="0.01"
            min="0"
            value={editRates.billable_rate}
            onChange={(e) =>
              setEditRates({ ...editRates, billable_rate: e.target.value })
            }
            className={`w-24 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-violet-500 ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'border-gray-300'}`}
          />
        ) : (
          <div
            className={`text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}
          >
            {currency} {Number(assignment.billable_rate || 0).toFixed(2)}
          </div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <button
                onClick={handleSave}
                className="p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-all"
                title={t('common.save')}
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </button>
              <button
                onClick={handleCancel}
                className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-slate-300 hover:bg-slate-700' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`}
                title={t('common.cancel')}
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setEditing(true)}
                className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                title={t('common.edit')}
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button
                onClick={onRemove}
                className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                title={t('pricelistDetail.remove')}
              >
                <svg
                  className="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export default PricelistDetailView;
